import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    TextField,
    Table,
    TableBody,
    TableRow,
    TableCell, MenuItem, Snackbar,
} from '@mui/material';
import { GetCookieInfo } from '../../../logic/CookieManager';
import {
    Black,
    StyledButtonOrange,
    StyledButtonContainer,
    StyledTableCell,
    StyledAlert
} from "../../../styles/MyStyles";
import {GetEnumMenuItems, GetEnumValueName, GetEnumValues} from "../../../logic/EnumHelper";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {DownloadFileFromBlob} from "../../../logic/FileDownloader";
import {GetClientViewName} from "../../../logic/ClientNameHelper";
import {GetClients} from "../../../logic/DataProvider";

const ManagerReportingPage = () => {
    const [snackbar, setSnackbar] = React.useState({
        open: false,
        vertical: 'top',
        horizontal: 'center',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [clients, setClients] = useState([]);
    const [reportArgs, setReportArgs] = useState({
        'type': '',
        'dateFrom': '',
        'dateTo': '',
        'contactId': '',
        'clientData': 0,
        'showOperationLimit': true
    });

    useEffect(() => {
        GetClients().then((clients) => setClients(clients));
    }, []);

    const validateForm = () => {
        let newErrors = {};

        if (reportArgs.type === '') {
            newErrors.type = 'Type is required.';
        } else {
            switch (reportArgs.type) {
                case 0:
                    if (reportArgs.dateFrom === '') {
                        newErrors.dateFrom = 'Date From is required.';
                    }
                    if (reportArgs.dateTo === '') {
                        newErrors.dateTo = 'Date To is required.';
                    }
                    break;
                case 1:
                case 2:
                case 3:
                    if (reportArgs.dateFrom === '') {
                        newErrors.dateFrom = 'Date From is required.';
                    }
                    if (reportArgs.dateTo === '') {
                        newErrors.dateTo = 'Date To is required.';
                    }
                    if (reportArgs.contactId === '') {
                        newErrors.contactId = 'Client is required.';
                    }
                    break;
            }
        }

        // Return the errors
        return newErrors;
    };

    const handleFieldChange = (event) => {
        setReportArgs({
            ...reportArgs,
            [event.target.name]: event.target.value,
        });
    };

    const generateReport = async () => {
        try {
            const newErrors = validateForm();

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }

            console.log(JSON.stringify(reportArgs));

            const tokenCookie = GetCookieInfo('nplt');

            let endpoint = getEndpoint();

            const response = await fetch(
                `${process.env.REACT_APP_REPORTING_URL}/${endpoint}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                    body: JSON.stringify(reportArgs),
                }
            );
            if (response.ok) {
                let fileName = GetEnumValueName('reportType', reportArgs.type) + '.pdf';
                await DownloadFileFromBlob(response, fileName);
                console.log('Report generating successfully.');
            } else {
                console.log('Report generating failed.');
                response.json().then((json) => {
                    setSnackbar({ ...snackbar, open: true, message: json.errorMessage})
                });
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const getEndpoint = () => {
        switch (reportArgs.type) {
            case 0:
                return 'clients-activity';
            case 1:
                return 'balance';
            case 2:
                return 'asset-movements';
            case 3:
                return 'Trades';
        }
        return 'undefined';
    }

    const getClientsMenuItems = () => {
        let items = [];
        if (clients) {
            clients.forEach((elem) => {
                items.push(
                    <MenuItem key={elem.id} value={elem.id}>
                        {GetClientViewName(elem)}
                    </MenuItem>
                );
            });
        }
        return items;
    }

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const getTypeMenuItems = () => {
        let enumElems = GetEnumValues('reportType');

        if (enumElems) {
            let items = [];

            for (const [key, value] of Object.entries(enumElems)) {
                let intKey = parseInt(key);

                if (intKey === 4 || intKey === 5) {
                    continue;
                }
                items.push(
                    <MenuItem key={intKey} value={intKey}>
                        {value}
                    </MenuItem>
                );
            }
            return items;
        }
    };

    return (
        <Container sx={{ margin: '0.2rem', minWidth: '99%' }}>
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}>
                <StyledAlert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                    {snackbar.message}
                </StyledAlert>
            </Snackbar>
            <Typography variant="h4" gutterBottom sx={{ color: Black }}>
                Report Generator
            </Typography>
            <StyledButtonContainer container>
                <StyledButtonOrange variant="contained"
                                    onClick={generateReport}
                                    disabled={reportArgs.type !== 0 && reportArgs.type !== 1 && reportArgs.type !== 2 && reportArgs.type !== 3}>
                    Generate
                </StyledButtonOrange>
            </StyledButtonContainer>
            <Table>
                <TableBody>
                    <TableRow>
                        <StyledTableCell style={{ width: '10%' }}>Report Type</StyledTableCell>
                        <TableCell style={{ width: '90%' }}>
                            <TextField
                                name="type"
                                label="Type"
                                select
                                value={reportArgs.type}
                                onChange={handleFieldChange}
                                fullWidth
                                required
                                error={!!errors.type}
                                helperText={errors.type}
                            >
                                {getTypeMenuItems()}
                            </TextField>
                        </TableCell>
                    </TableRow>
                    {(reportArgs.type === 0 || reportArgs.type === 1 || reportArgs.type === 2 || reportArgs.type === 3) && (
                        <>
                            <TableRow>
                                <StyledTableCell style={{ width: '10%' }}>Date From</StyledTableCell>
                                <TableCell style={{ width: '90%' }}>
                                    <DatePicker
                                        timezone="UTC"
                                        name="dateFrom"
                                        label="Date"
                                        maxDate={dayjs(reportArgs.dateTo)}
                                        value={dayjs(reportArgs.dateFrom)}
                                        onChange={(date) =>  {
                                            let event = {
                                                'target': {
                                                    'name': 'dateFrom',
                                                    'value': date.toJSON()
                                                }
                                            };
                                            handleFieldChange(event);
                                        }
                                        }
                                        fullWidth
                                        size='small'
                                        slotProps={{
                                            textField: {
                                                required: true,
                                            },
                                        }}
                                        error={!!errors.dateFrom}
                                        helperText={errors.dateFrom}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell style={{ width: '10%' }}>Date To</StyledTableCell>
                                <TableCell style={{ width: '90%' }}>
                                    <DatePicker
                                        timezone="UTC"
                                        name="dateTo"
                                        label="Date"
                                        minDate={dayjs(reportArgs.dateFrom)}
                                        value={dayjs(reportArgs.dateTo)}
                                        onChange={(date) =>  {
                                            let event = {
                                                'target': {
                                                    'name': 'dateTo',
                                                    'value': date.toJSON()
                                                }
                                            };
                                            handleFieldChange(event);
                                        }
                                        }
                                        fullWidth
                                        size='small'
                                        slotProps={{
                                            textField: {
                                                required: true,
                                            },
                                        }}
                                        error={!!errors.dateTo}
                                        helperText={errors.dateTo}
                                    />
                                </TableCell>
                            </TableRow>
                        </>
                    )}
                    {(reportArgs.type === 1 || reportArgs.type === 2 || reportArgs.type === 3) && (
                        <>
                            <TableRow>
                                <StyledTableCell style={{ width: '10%' }}>Client</StyledTableCell>
                                <TableCell style={{ width: '90%' }}>
                                    <TextField
                                        name="contactId"
                                        label="Client"
                                        select
                                        value={reportArgs.contactId}
                                        onChange={handleFieldChange}
                                        fullWidth
                                        required
                                        error={!!errors.contactId}
                                        helperText={errors.contactId}
                                    >
                                        {getClientsMenuItems()}
                                    </TextField>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell style={{ width: '10%' }}>Client info in report</StyledTableCell>
                                <TableCell style={{ width: '90%' }}>
                                    <TextField
                                        name="clientData"
                                        label="Client Info"
                                        select
                                        value={reportArgs.clientData}
                                        onChange={handleFieldChange}
                                        fullWidth
                                        required
                                        error={!!errors.clientData}
                                        helperText={errors.clientData}
                                    >
                                        {GetEnumMenuItems('reportClientData')}
                                    </TextField>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell style={{ width: '10%' }}>Show operation limit</StyledTableCell>
                                <TableCell style={{ width: '90%' }}>
                                    <TextField
                                        name="showOperationLimit"
                                        label="Show operation limit"
                                        value={reportArgs.showOperationLimit}
                                        onChange={handleFieldChange}
                                        fullWidth
                                        size='small'
                                        select
                                    >
                                        <MenuItem value={false}>No</MenuItem>
                                        <MenuItem value={true}>Yes</MenuItem>
                                    </TextField>
                                </TableCell>
                            </TableRow>
                        </>
                    )}
                </TableBody>
            </Table>
        </Container>
    );
};

export default ManagerReportingPage;
